import { Alert, Button, Checkbox, NumberInput, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { odpriPdf } from '../api/pdf';
import { datum, datumZaApi } from '../oblika/oblika';
import { imeOsebe } from '../osebe/vrste';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import {
  KlavzuleIzdaje,
  KorakiIzdaje,
  Naprej,
  OsebeIzdaje,
  StoritveIzdaje,
  TabelaStoritev,
  TabelaVozil,
  VozilaIzdaje,
  VrsticaIzdaje,
} from './Gradniki';
import { korakiZNapakami, korakPolja, prvaNapaka, stevilka, type Korak } from './izdaja';

type Zahteva = Shema['PredracunZahteva'];
type Oseba = Shema['OsebaPodatki'];

/**
 * Nov predračun – kot KreirajPredracun v Swingu (koraki Kupec, Artikli, Storitve, Roki, Predogled). Cene postavk,
 * »velja do«, DDV, zneske in napake pokaže izračun strežnika ({@code POST /api/predracuni/izracun}). Z {@code ?nalog=id}
 * (modul Delavnica) prevzame stranko in postavke delovnega naloga.
 */
export function NovPredracun() {
  const [iskanje] = useSearchParams();
  const nalog = iskanje.get('nalog') ? Number(iskanje.get('nalog')) : null;
  const osnutek = useQuery({
    queryKey: ['predracun-osnutek', nalog],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/predracuni/nov', { signal, params: { query: { nalog: nalog ?? undefined } } })),
    staleTime: Infinity,
    gcTime: 0,
  });
  if (!osnutek.data) {
    return (
      <StanjeNalaganja
        napaka={osnutek.error}
        ponovi={() => void osnutek.refetch()}
        naslov="Novega predračuna ni bilo mogoče pripraviti"
      />
    );
  }
  return <ObrazecPredracuna osnutek={osnutek.data} />;
}

function ObrazecPredracuna({ osnutek }: { osnutek: Shema['PredracunOsnutek'] }) {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const [vnos, setVnos] = useState<Zahteva>(() => ({ ...osnutek.zahteva }));
  const [spremenjeno, setSpremenjeno] = useState(false);
  const [korak, setKorak] = useState<Korak>(osnutek.zahteva?.delovniNalogId ? 'predogled' : 'kupec');
  const [poskus, setPoskus] = useState(false);
  const [strezniske, setStrezniske] = useState<Record<string, string>>({});
  const [razlog, setRazlog] = useState<string | null>(null);
  const [dela, setDela] = useState<'predogled' | 'izdaja' | null>(null);
  const [osebe, setOsebe] = useState<{ kupec?: Oseba | null; uporabnik?: Oseba | null }>({});

  const [zakasnjen] = useDebouncedValue(vnos, 250);
  const izracun = useQuery({
    queryKey: ['izracun', 'predracun', zakasnjen],
    queryFn: ({ signal }) => podatki(api.POST('/api/predracuni/izracun', { signal, body: zakasnjen })),
    placeholderData: keepPreviousData,
  });
  const iz = izracun.data;
  const z = iz?.zahteva ?? vnos;
  const napake = poskus ? { ...(iz?.napake ?? {}), ...strezniske } : {};
  const aktualno = !izracun.isFetching && !izracun.isPlaceholderData && zakasnjen === vnos;

  useEffect(() => {
    if (!spremenjeno) {
      return;
    }
    const vprasaj = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', vprasaj);
    return () => window.removeEventListener('beforeunload', vprasaj);
  }, [spremenjeno]);

  const nastavi = (sprememba: Partial<Zahteva>) => {
    setVnos((v) => ({ ...v, ...sprememba }));
    setSpremenjeno(true);
    setStrezniske({});
    setRazlog(null);
  };

  const kupec = osebe.kupec !== undefined ? osebe.kupec : iz?.kupec;
  const uporabnik = osebe.uporabnik !== undefined ? osebe.uporabnik : iz?.uporabnikVozila;
  const vozila = iz?.vozila ?? [];
  const storitve = z.storitve ?? [];

  const pokaziNapake = (n: Record<string, string>) => {
    setPoskus(true);
    const prva = prvaNapaka(n);
    setRazlog(prva?.sporocilo ?? null);
    if (prva) {
      setKorak(korakPolja(prva.polje));
    }
  };

  const predogled = async () => {
    setDela('predogled');
    setRazlog(null);
    try {
      await odpriPdf(api.POST('/api/predracuni/predogled/pdf', { body: vnos, parseAs: 'blob' }));
    } catch (e) {
      setRazlog(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  // kot Swing: izda predračun, odpre njegov PDF, nato podrobnosti predračuna
  const izdaj = async () => {
    if (aktualno && iz && Object.keys(iz.napake ?? {}).length > 0) {
      pokaziNapake(iz.napake ?? {});
      return;
    }
    setDela('izdaja');
    setRazlog(null);
    const izid: { p?: Shema['PredracunPodrobnosti'] } = {};
    try {
      await odpriPdf(
        (async () => {
          izid.p = await podatki(api.POST('/api/predracuni', { body: vnos }));
          return api.GET('/api/predracuni/{id}/pdf', { params: { path: { id: izid.p.id! } }, parseAs: 'blob' });
        })(),
      );
    } catch (e) {
      if (!izid.p) {
        setDela(null);
        if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
          setStrezniske(e.polja);
          pokaziNapake(e.polja);
        } else {
          setRazlog(sporocilo(e));
        }
        return;
      }
      notifications.show({ color: 'red', message: `PDF predračuna ni bil odprt: ${sporocilo(e)}` });
    }
    const p = izid.p!;
    setSpremenjeno(false);
    await odjemalec.invalidateQueries({ queryKey: ['predracuni'] });
    await odjemalec.invalidateQueries({ queryKey: ['delovni-nalog'] });
    notifications.show({ color: 'green', message: `Predračun ${p.stPredracuna} je izdan.` });
    setDela(null);
    void navigate(`/predracuni/${p.id}`, { replace: true });
  };

  const vsebina: Record<Korak, React.ReactNode> = {
    kupec: (
      <>
        {osnutek.stNaloga && (
          <Alert color="blue" variant="light" mb="md">
            Predračun iz delovnega naloga {osnutek.stNaloga}: stranka in postavke so prevzete.
          </Alert>
        )}
        <OsebeIzdaje
          kupec={kupec}
          uporabnik={uporabnik}
          izberiKupca={(o) => {
            setOsebe((s) => ({ ...s, kupec: o }));
            nastavi({ kupecId: o?.id });
          }}
          izberiUporabnika={(o) => {
            setOsebe((s) => ({ ...s, uporabnik: o }));
            nastavi({ uporabnikVozilaId: o?.id });
          }}
          napake={napake}
          zVozili={(vnos.vozila ?? []).length > 0}
        />
        <Naprej na="artikli" izberi={setKorak} />
      </>
    ),
    artikli: (
      <>
        <VozilaIzdaje
          financirana
          vozila={vozila}
          dodaj={(v) => nastavi({ vozila: [...(vnos.vozila ?? []), { voziloId: v.id, cena: v.cena }] })}
          odstrani={(i) => nastavi({ vozila: (vnos.vozila ?? []).filter((_, j) => j !== i) })}
          napaka={napake.vozila ?? napake.postavke}
        />
        <Naprej na="storitve" izberi={setKorak} />
      </>
    ),
    storitve: (
      <>
        <StoritveIzdaje
          storitve={storitve}
          dodaj={(s) => nastavi({ storitve: [...(vnos.storitve ?? []), s] })}
          odstrani={(i) => nastavi({ storitve: (vnos.storitve ?? []).filter((_, j) => j !== i) })}
          napaka={napake.storitve ?? napake.postavke}
        />
        <Naprej na="roki" izberi={setKorak} />
      </>
    ),
    roki: (
      <>
        <Kartica naslov="Roki, povezani s predračunom">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" maw={640}>
            <DatePickerInput
              label="Datum"
              value={vnos.datum ?? null}
              onChange={(d) => nastavi({ datum: datumZaApi(d) })}
              valueFormat="D. M. YYYY"
              error={napake.datum}
            />
            <NumberInput
              label="Rok veljave (dni)"
              value={vnos.veljavnoDni ?? 0}
              onChange={(v) => nastavi({ veljavnoDni: stevilka(v) ?? 0 })}
              allowDecimal={false}
              min={0}
              error={napake.veljavnoDni}
              size="md"
            />
            <div>
              <Text size="sm" fw={500}>
                Velja do
              </Text>
              <Text size="lg" fw={700} mt={6}>
                {datum(z.veljavnoDo) || '–'}
              </Text>
            </div>
          </SimpleGrid>
        </Kartica>
        <Naprej na="predogled" izberi={setKorak} />
      </>
    ),
    predogled: (
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica naslov="Podatki predračuna">
            <Stack gap="sm">
              <Podatki
                vrstice={[
                  ['Kupec', imeOsebe(kupec ?? undefined)],
                  ['Uporabnik vozila', imeOsebe(uporabnik ?? undefined)],
                  ['Številka', iz?.stevilka],
                  ['Velja do', datum(z.veljavnoDo)],
                ]}
              />
              <Checkbox
                label="Izkaži DDV"
                checked={!!(iz?.izkaziDdvOmogocen ? vnos.izkaziDdv : z.izkaziDdv)}
                disabled={!iz?.izkaziDdvOmogocen}
                onChange={(e) => nastavi({ izkaziDdv: e.currentTarget.checked })}
              />
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                <TextInput
                  label="Referenca za plačilo"
                  description="Prazno = številka predračuna"
                  value={vnos.referencaZaPlacilo ?? ''}
                  onChange={(e) => nastavi({ referencaZaPlacilo: e.currentTarget.value.toUpperCase() })}
                  maxLength={26}
                  error={napake.referencaZaPlacilo}
                />
                <TextInput
                  label="Koda namena"
                  description="Npr. GDSV"
                  value={vnos.paymentClassification ?? ''}
                  onChange={(e) => nastavi({ paymentClassification: e.currentTarget.value.toUpperCase() })}
                  maxLength={4}
                  error={napake.paymentClassification}
                />
              </SimpleGrid>
            </Stack>
          </Kartica>
          <KlavzuleIzdaje klavzule={vnos.klavzule ?? []} nastavi={(k) => nastavi({ klavzule: k })} />
        </Stack>
        <Stack gap="md">
          <Kartica naslov="Storitve">
            <TabelaStoritev storitve={storitve} />
          </Kartica>
          <Kartica naslov="Artikli">
            <TabelaVozil vozila={vozila} />
          </Kartica>
        </Stack>
      </SimpleGrid>
    ),
  };

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/predracuni"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Predračuni
      </Button>
      <GlavaZaslona
        naslov="Nov predračun"
        opis="Izberite kupca, dodajte vozila in storitve, določite roke ter predračun preglejte in izdajte."
      />
      <KorakiIzdaje korak={korak} izberi={setKorak} napake={korakiZNapakami(napake)} vsebina={vsebina} />
      <VrsticaIzdaje
        znesek={iz?.znesek}
        razlog={razlog}
        predogled={() => void predogled()}
        izdaj={() => void izdaj()}
        naziv="Izdaj predračun"
        dela={dela}
      />
    </ZaslonSeznama>
  );
}
